import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import axios from 'axios'
import { useParams } from 'react-router-dom'

import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Checkbox } from '../../ui/checkbox'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'

function KlassPackageRegistrationForm({ form, onSubmit, isSubmitting, demo = false }) {
  const { facility_id: facilityId } = useParams()
  const [packages, setPackages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { control, handleSubmit } = form

  useEffect(() => {
    async function fetchPackages() {
      try {
        const response = await axios.get(`/api/facilities/${facilityId}/class_packages`)
        const packageData = response.data.data.map((pkg) => ({
          id: parseInt(pkg.id, 10),
          title: pkg.attributes.title
        }))
        setPackages(packageData)
      } catch (error) {
        console.error('Failed to load class packages:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (facilityId) {
      fetchPackages()
    } else {
      setIsLoading(false)
    }
  }, [facilityId])

  if (!facilityId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>This report is only available when viewing a specific facility.</p>
        <p className="text-sm mt-2">Please access this report from a facility&apos;s reports page.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label>
            Class Package <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="klass_package_id"
            control={control}
            rules={{ required: 'Please select a class package' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  value={field.value ? String(field.value) : ''}
                  onValueChange={(val) => field.onChange(parseInt(val, 10))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a class package" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.id} value={String(pkg.id)}>
                        {pkg.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldState.error && <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>}
              </>
            )}
          />
          <p className="text-sm text-gray-500 mt-1">Select a class package to see all students registered for it.</p>
        </div>

        <div>
          <Label htmlFor="file_format">File Format</Label>
          <Controller
            name="file_format"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xls">Excel (XLS)</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div hidden={demo}>
          <Label>Delivery Method</Label>
          <Controller
            name="delivery_method"
            control={control}
            render={({ field }) => (
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="email"
                    checked={field.value === 'email' || field.value === 'both'}
                    disabled={field.value === 'email'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'download') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('email')
                      } else if (field.value === 'both') {
                        field.onChange('download')
                      }
                    }}
                  />
                  <Label htmlFor="email" className="font-normal cursor-pointer">
                    Email me when ready
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="download"
                    checked={field.value === 'download' || field.value === 'both'}
                    disabled={field.value === 'download'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'email') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('download')
                      } else if (field.value === 'both') {
                        field.onChange('email')
                      }
                    }}
                  />
                  <Label htmlFor="download" className="font-normal cursor-pointer">
                    Available for download
                  </Label>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Generating Preview...' : 'Preview Report'}
      </Button>
    </form>
  )
}

KlassPackageRegistrationForm.propTypes = {
  demo: PropTypes.bool,
  form: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired
}

export default KlassPackageRegistrationForm
